/**
 * What an entry form does around a dependent choice, worked out without a
 * form: `docs/plans/dependent-choices-and-multi-select.md`, Decisions 1, 2, 3
 * and 6. The phone's form and the web's run these same rules through their
 * own hands (`ChoiceMoves`), and the server hides a field by the same
 * `hidesChild` the forms do.
 *
 * A Sub-category holds a filter: offer only the Sub-categories whose Category
 * is the Category this entry holds. The rule that decides "does this row
 * belong" is `rowMatches` in formula.ts, the one the server judges a write by,
 * so a form can never let through a pair the server will refuse, nor refuse
 * one the server would keep. Everything here only asks it.
 *
 *   a parent changes      the children it no longer covers come out, with a
 *                         note saying why, and down a chain (`trimChild`)
 *   a child is picked     an empty parent takes the child's parent, and a
 *                         multi select parent gains it (`parentFill`)
 *   a parent has no rows  an empty child hides, and is not required (`hidesChild`)
 *
 * `settleChoices` walks those over a form's hands; `choice-form.test.ts`
 * walks every design board through it with a form made of maps.
 */
import type { EntryValues } from './api';
import { choiceOf, rowMatches, type ChoiceFilter, type FieldSpec } from './formula';
import { t } from './i18n/t';

/**
 * The part of a field these rules read. `RecordSchemaField` is one, and so is
 * the server's write-side `ChoiceFieldDef`.
 */
export type ChoiceFormField = {
  readonly id: string;
  readonly label: string;
  readonly type: string;
  readonly targetRecordId: string | null;
  readonly filter?: ChoiceFilter | null;
  readonly spec?: FieldSpec | null;
};

/**
 * One filtered field on the form, and the field it follows.
 *
 * `childId` is the field that holds the filter (Sub-category). `parentId` is the
 * filter's `equals`, the field on this row it follows (Category).
 * `filterFieldId` is the filter's `field`, on the target list (the Category
 * column of Sub-categories).
 */
export type ChoiceLink = {
  readonly childId: string;
  readonly childLabel: string;
  readonly childIsMany: boolean;
  /** The list the child picks from. */
  readonly targetRecordId: string;
  readonly parentId: string;
  readonly parentLabel: string;
  readonly parentIsMany: boolean;
  /** The list the parent picks from, which is the list `filterFieldId` points at. */
  readonly parentTargetRecordId: string | null;
  readonly filterFieldId: string;
  /**
   * True when the parent is a field of the row a line hangs from, not of the
   * line itself. The line follows it, but never fills it: a guest picked
   * first does not move the stay's own choice.
   */
  readonly parentAbove?: true;
};

/**
 * Whether a row's parent is known. `undefined` means the form cannot say —
 * the row is not in the device's mirror and did not travel with the schema —
 * and an unknown row is never cleared or refused by a form: the server judges
 * it on save.
 */
export type ParentOf = (rowId: string) => string | null | undefined;

const isChoiceType = (type: string): boolean => type === 'reference' || type === 'multi_select';

/** The filter a field holds: the schema's own `filter`, or the one in its spec. */
export const filterOf = (field: ChoiceFormField): ChoiceFilter | null =>
  isChoiceType(field.type) ? (field.filter ?? choiceOf(field.spec)?.filter ?? null) : null;

/**
 * Every filtered field on this form whose parent is on the form too.
 *
 * A parent this person may not write is not drawn, so a child following it
 * follows nothing on this form and is drawn as an ordinary picker; the server
 * still judges the pair against the stored parent. A filter whose `equals`
 * names itself, or a field that is not a reference or a multi select, links
 * nothing: the server refuses one, and an old schema holding one is no reason
 * to stall a form.
 */
export const choiceLinksOf = (
  fields: readonly ChoiceFormField[],
  /**
   * On a line: the fields of the row it hangs from. A line's filter may
   * follow one of them (`ChoiceFilter`), and the link is then marked
   * `parentAbove`. They are never children here: that row settles its own.
   */
  above: readonly ChoiceFormField[] = [],
): readonly ChoiceLink[] => {
  const byId = new Map(fields.map((field) => [field.id, field]));
  const aboveById = new Map(above.map((field) => [field.id, field]));
  const links: ChoiceLink[] = [];
  for (const child of fields) {
    const filter = filterOf(child);
    if (filter === null || child.targetRecordId === null) continue;
    const own = byId.get(filter.equals);
    const parent = own ?? aboveById.get(filter.equals);
    if (parent === undefined || parent.id === child.id || !isChoiceType(parent.type)) continue;
    links.push({
      ...(own === undefined ? { parentAbove: true as const } : {}),
      childId: child.id,
      childLabel: child.label,
      childIsMany: child.type === 'multi_select',
      targetRecordId: child.targetRecordId,
      parentId: parent.id,
      parentLabel: parent.label,
      parentIsMany: parent.type === 'multi_select',
      parentTargetRecordId: parent.targetRecordId,
      filterFieldId: filter.field,
    });
  }
  return links;
};

/** The row ids a reference or a multi select value holds, empties dropped. */
export const idsOf = (value: unknown): readonly string[] => {
  if (typeof value === 'string') return value === '' ? [] : [value];
  if (Array.isArray(value)) return value.filter((one): one is string => typeof one === 'string' && one !== '');
  return [];
};

const filterFor = (link: ChoiceLink): ChoiceFilter => ({ field: link.filterFieldId, equals: link.parentId });

/**
 * Whether a row filed under `ownParent` may be offered while the parent field
 * holds `parentValue`: `rowMatches`, asked of one row. With no parent chosen
 * every row may.
 */
export const underParent = (link: ChoiceLink, ownParent: string | null, parentValue: unknown): boolean =>
  rowMatches(filterFor(link), { [link.filterFieldId]: ownParent }, { [link.parentId]: parentValue });

/**
 * Does one child row belong under what the parent holds? The shared rule, fed
 * the one value of the target row it reads. Unknown rows answer true.
 */
export const belongs = (link: ChoiceLink, rowId: string, parentValue: unknown, parentOf: ParentOf): boolean => {
  const own = parentOf(rowId);
  if (own === undefined) return true;
  return underParent(link, own, parentValue);
};

/** What a parent change did to one child, so the form can say so under it. */
export type ChildTrim = {
  /** What the child holds now. */
  readonly value: string | readonly string[] | null;
  /** How many rows came out. */
  readonly removed: number;
  /** The rows that came out, so whatever follows the child can drop theirs. */
  readonly dropped: readonly string[];
};

/**
 * The child after its parent changed, or null when nothing in it had to go.
 *
 * Decision 2: a reference whose row is not under the new parent is cleared.
 * Decision 6: a multi select loses only the rows not under the new parents,
 * and keeps the rest in the order they were picked. An empty parent keeps
 * everything, because with no parent every row matches.
 *
 * `dropped` is the rows the FORM just took out of the parent, down a chain: a
 * new Category empties the Sub-category, and the Sub-sub-category that sat
 * under the old Sub-category goes with it, although an empty parent would
 * otherwise keep every row. Keeping it would leave a saved triple that does
 * not belong together.
 */
export const trimChild = (
  link: ChoiceLink,
  childValue: unknown,
  parentValue: unknown,
  parentOf: ParentOf,
  dropped: readonly string[] = [],
): ChildTrim | null => {
  const held = idsOf(childValue);
  if (held.length === 0) return null;
  const keep = held.filter((id) => {
    const own = parentOf(id);
    if (typeof own === 'string' && dropped.includes(own)) return false;
    return belongs(link, id, parentValue, parentOf);
  });
  if (keep.length === held.length) return null;
  const removed = held.length - keep.length;
  const gone = held.filter((id) => !keep.includes(id));
  if (!link.childIsMany) return { value: null, removed, dropped: gone };
  return { value: keep.length === 0 ? null : keep, removed, dropped: gone };
};

/**
 * The first row the child holds that is not under its parent, for an edit of
 * an entry whose pair no longer matches because a row moved (Decision 2). Null
 * when the pair is good.
 */
export const mismatchOf = (
  link: ChoiceLink,
  childValue: unknown,
  parentValue: unknown,
  parentOf: ParentOf,
): { readonly rowId: string; readonly parentId: string | null } | null => {
  for (const id of idsOf(childValue)) {
    if (belongs(link, id, parentValue, parentOf)) continue;
    return { rowId: id, parentId: parentOf(id) ?? null };
  }
  return null;
};

/**
 * What the parent becomes once a child was picked first, or undefined for no
 * change. Decision 2 and the owner's decision of 4 Oct: picking a sub-category
 * with no category fills the category from the row.
 *
 * A reference parent fills only while it is empty, so it never overwrites a
 * choice, and only when every row the child holds sits under the same parent —
 * two services under two types cannot pick one type. A multi select parent
 * gains each parent it does not hold yet, which is what keeps the pair whole
 * (Salon-Service-First).
 */
export const parentFill = (
  link: ChoiceLink,
  childValue: unknown,
  parentValue: unknown,
  parentOf: ParentOf,
): string | readonly string[] | undefined => {
  const owners: string[] = [];
  for (const id of idsOf(childValue)) {
    const own = parentOf(id);
    if (typeof own === 'string' && own !== '' && !owners.includes(own)) owners.push(own);
  }
  if (owners.length === 0) return undefined;
  const held = idsOf(parentValue);
  if (!link.parentIsMany) {
    if (held.length > 0 || owners.length !== 1) return undefined;
    return owners[0];
  }
  const missing = owners.filter((id) => !held.includes(id));
  return missing.length === 0 ? undefined : [...held, ...missing];
};

/**
 * Whether the child hides: it is empty, and its parent holds one row and that
 * row has no children (Expense-No-Subcategories). An automatic "show when", so
 * `required` does not apply to a hidden child — on either form, nor on the
 * server.
 *
 * A child that holds a row never hides (Decision 2: an old entry keeps an
 * archived category or sub-category, and shows it as archived). When the
 * person changes the parent, the form clears the row that is not under it
 * first, and the emptied child then hides.
 *
 * `count` is how many live rows of the child's list sit under that row; only
 * whether it is zero is read. Null means the caller cannot count the list,
 * and then the child stays: hiding a field the person may need is worse than
 * an empty sheet. A multi select parent never hides its child: its rows may
 * sit under any of the parents chosen. An empty child whose own parent is
 * hidden hides with it.
 */
export const hidesChild = (
  link: ChoiceLink,
  parentValue: unknown,
  childValue: unknown,
  count: number | null,
  parentHidden: boolean,
): boolean => {
  if (idsOf(childValue).length > 0) return false;
  if (parentHidden) return true;
  if (link.parentIsMany) return false;
  return idsOf(parentValue).length === 1 && count === 0;
};

/** How many rows sit under the parent, asked only where the answer can hide the child. */
const countWhereItHides = (
  link: ChoiceLink,
  parentValue: unknown,
  childValue: unknown,
  count: (link: ChoiceLink, parentValue: unknown) => number | null,
): number | null =>
  link.parentIsMany || idsOf(childValue).length > 0 || idsOf(parentValue).length !== 1 ? null : count(link, parentValue);

/**
 * The children hidden by what their parents hold, for a form that opens on
 * values and for a write the server judges. A chain hides from the top down:
 * an empty Sub-sub-category hides with the Sub-category hidden above it. A
 * child that holds a row is never hidden.
 */
export const hiddenChildrenOf = (
  links: readonly ChoiceLink[],
  valueOf: (fieldId: string) => unknown,
  count: (link: ChoiceLink, parentValue: unknown) => number | null,
): Set<string> => {
  const hidden = new Set<string>();
  // Go round until nothing moves: a link may come before the one above it.
  for (let pass = 0; pass <= links.length; pass += 1) {
    let moved = false;
    for (const link of links) {
      if (hidden.has(link.childId)) continue;
      const parentHidden = hidden.has(link.parentId);
      const parentValue = parentHidden ? null : valueOf(link.parentId);
      const childValue = valueOf(link.childId);
      if (hidesChild(link, parentValue, childValue, countWhereItHides(link, parentValue, childValue, count), parentHidden)) {
        hidden.add(link.childId);
        moved = true;
      }
    }
    if (!moved) break;
  }
  return hidden;
};

/**
 * The values a row made from this picker starts with: the parent it is filed
 * under (Decision 2, a new sub-category gets the current Category). Null when
 * the parent holds nothing, or several, and the row has nowhere to go — the
 * picker then asks for the parent first.
 */
export const createValuesOf = (link: ChoiceLink, parentValue: unknown): EntryValues | null => {
  const held = idsOf(parentValue);
  return held.length === 1 ? ({ [link.filterFieldId]: held[0] } as EntryValues) : null;
};

/** The parents a list read is narrowed to: the target list's field, and the parent ids. */
export type ParentsFilter = { readonly fieldId: string; readonly values: readonly string[] };

/**
 * The server's rows kept to the filter, for a list the device does not hold.
 * Each entry carries its own values, so the same rule judges it.
 */
export const keepMatching = <T extends { readonly values: Readonly<Record<string, unknown>> }>(
  entries: readonly T[],
  filter: ParentsFilter | null | undefined,
): readonly T[] => {
  if (filter === null || filter === undefined || filter.values.length === 0) return entries;
  const rule: ChoiceFilter = { field: filter.fieldId, equals: '$parent' };
  const wanted = { $parent: [...filter.values] };
  return entries.filter((entry) => rowMatches(rule, entry.values, wanted));
};

/** One page of a list from the server, as `GET /v1/records/:id/entries` answers it. */
export type ListPage<T> = {
  readonly entries: readonly T[];
  readonly references: Readonly<Record<string, string>>;
  readonly cursor: string | null;
};

/** The page a filtered server search reads: the most the entries route answers with at once. */
export const FILTERED_SEARCH_PAGE = 200;

/** How many pages a filtered server search reads before it answers with what it found. */
export const FILTERED_SEARCH_MAX_PAGES = 20;

/**
 * A typed word's rows from the server, for a list the device does not hold,
 * kept to the filter. The server cannot narrow by a parent, so a filtered
 * search reads on, page after page, until it holds `limit` rows under the
 * parents or the list runs out: the rows under a parent need not sit on the
 * first page. A search with no filter, or with an empty parent, keeps every
 * row, so one page is the answer. The references of every page read come
 * back together, so each row's title can be drawn from them.
 *
 * The phone's search and the web's both read through this, so the two cannot
 * stop at different pages.
 */
export const searchMatchingPages = async <T extends { readonly values: Readonly<Record<string, unknown>> }>(
  fetchPage: (cursor: string | null) => Promise<ListPage<T>>,
  filter: ParentsFilter | null | undefined,
  limit: number,
  maxPages: number = FILTERED_SEARCH_MAX_PAGES,
): Promise<{ readonly entries: readonly T[]; readonly references: Readonly<Record<string, string>> }> => {
  const first = await fetchPage(null);
  if (filter === null || filter === undefined || filter.values.length === 0) {
    return { entries: first.entries, references: first.references };
  }
  const entries: T[] = [];
  let references: Readonly<Record<string, string>> = {};
  for (let page: ListPage<T> = first, read = 1; ; read += 1) {
    references = { ...references, ...page.references };
    entries.push(...keepMatching(page.entries, filter));
    if (entries.length >= limit || page.cursor === null || read >= maxPages) break;
    page = await fetchPage(page.cursor);
  }
  return { entries: entries.slice(0, limit), references };
};

/* ------------------------------------------------------------ the settling */

/** The links of one form, looked up both ways. */
export type ChoiceGraph = {
  readonly links: readonly ChoiceLink[];
  readonly linkOfChild: ReadonlyMap<string, ChoiceLink>;
  readonly linksOfParent: ReadonlyMap<string, readonly ChoiceLink[]>;
};

export const choiceGraphOf = (
  fields: readonly ChoiceFormField[],
  /** On a line: the fields of the row it hangs from (`choiceLinksOf`). */
  above: readonly ChoiceFormField[] = [],
): ChoiceGraph => {
  const links = choiceLinksOf(fields, above);
  const linksOfParent = new Map<string, ChoiceLink[]>();
  for (const link of links) linksOfParent.set(link.parentId, [...(linksOfParent.get(link.parentId) ?? []), link]);
  return { links, linkOfChild: new Map(links.map((link) => [link.childId, link])), linksOfParent };
};

/** A row another row is filed under, titled: what a child says it is narrowed by. */
export type ChoiceParent = { readonly id: string; readonly title: string };

/** What the form says on a field after a settle. Absent keys are left as they were. */
export type ChoiceDress = {
  readonly note?: string | null;
  readonly caption?: string | null;
  readonly parents?: readonly ChoiceParent[];
  readonly isHidden?: boolean;
};

/** The sentences a settle puts under a field. */
export type ChoiceWords = {
  /** "Cleared because Category changed." */
  readonly cleared: (link: ChoiceLink) => string;
  /** "Removed 1 from Services, not under the chosen Service types." */
  readonly removed: (link: ChoiceLink, count: number) => string;
  /** The caption of a parent filled from its child: "From Sub-category". */
  readonly filledFrom: (link: ChoiceLink) => string;
};

/** The catalogue's words for a settle, the same on the phone and the web. */
export const choiceWords: ChoiceWords = {
  cleared: (link) => t('choice.cleared_because', { field: link.parentLabel }),
  removed: (link, count) => t('choice.removed_children', { count, from: link.childLabel, field: link.parentLabel }),
  filledFrom: (link) => t('entry.from_default', { field: link.childLabel }),
};

/**
 * The form's hands, as `settleChoices` needs them. Each form answers them
 * from its handles and its field wrappers; a test answers them from a map,
 * which is how the boards are proved without a device.
 */
export type ChoiceMoves = {
  /** What a field holds now. A hidden field holds nothing. */
  readonly valueOf: (fieldId: string) => unknown;
  readonly isHidden: (fieldId: string) => boolean;
  /** Hides or shows a field. Either way it holds nothing after: shown again, it starts empty. */
  readonly setHidden: (fieldId: string, hides: boolean) => void;
  /** Puts a value in a field without the person typing it. */
  readonly reset: (fieldId: string, value: string | readonly string[] | null) => void;
  readonly tell: (fieldId: string, dress: ChoiceDress) => void;
  readonly parentOf: (link: ChoiceLink, ids: readonly string[]) => ParentOf;
  /**
   * How many rows of the child's list sit under the one row the parent
   * holds; null when the form cannot say. Asked only of a single parent that
   * holds one row.
   */
  readonly count: (link: ChoiceLink, parentValue: unknown) => number | null;
  readonly parentsOf: (link: ChoiceLink, parentValue: unknown) => readonly ChoiceParent[];
  /** Refills the defaults that read a field, and names the fields it filled. */
  readonly fillDefaults: (fieldId: string) => readonly string[];
  /** Hands a field the titles of a value somebody else put in it. */
  readonly knowTitles: (fieldId: string, value: unknown) => void;
  readonly words: ChoiceWords;
};

/**
 * A choice moved: settle what follows it, and what it follows.
 *
 * Decisions 2 and 6: a parent that changed clears the child whose row is not
 * under it, or takes out of a multi select only the rows that are not, and
 * says so under the child; down a chain, the rows under a cleared row go too.
 * A parent holding a row with no children hides the child once it is empty;
 * a parent changed again shows it, empty. Decision 3 and the owner's decision of 4 Oct: a
 * child picked first fills its parent from the row, and up a chain.
 *
 * `byPerson` is true when the person picked `changed`: the note under it has
 * been answered. `seen` stops a chain from walking back into a field it has
 * already settled — the parent filled from a child does not then trim that
 * child.
 */
export const settleChoices = (
  graph: ChoiceGraph,
  moves: ChoiceMoves,
  changed: string,
  byPerson: boolean,
  seen: Set<string> = new Set(),
  dropped: readonly string[] = [],
): void => {
  if (graph.links.length === 0 || seen.has(changed)) return;
  seen.add(changed);

  const asChild = graph.linkOfChild.get(changed);
  if (asChild !== undefined) {
    if (byPerson) moves.tell(changed, { note: null });
    const childValue = moves.valueOf(changed);
    const fill = parentFill(
      asChild,
      childValue,
      moves.valueOf(asChild.parentId),
      moves.parentOf(asChild, idsOf(childValue)),
    );
    // A parent above a line is that row's to set, not the line's.
    if (fill !== undefined && asChild.parentAbove !== true && !moves.isHidden(asChild.parentId)) {
      // The titles first, so a picker reset to an id can draw its name.
      moves.knowTitles(asChild.parentId, fill);
      moves.reset(asChild.parentId, fill);
      moves.tell(asChild.parentId, { caption: moves.words.filledFrom(asChild) });
      for (const filled of moves.fillDefaults(asChild.parentId)) settleChoices(graph, moves, filled, false, seen);
      settleChoices(graph, moves, asChild.parentId, false, seen);
    }
  }

  for (const link of graph.linksOfParent.get(changed) ?? []) {
    const parentHidden = moves.isHidden(changed);
    const parentValue = moves.valueOf(changed);
    const parents = moves.parentsOf(link, parentValue);
    const wasHidden = moves.isHidden(link.childId);
    // What is not under the parent comes out first: a child that holds a row
    // never hides, and one emptied here may.
    const childValue = moves.valueOf(link.childId);
    const trim = wasHidden ? null : trimChild(link, childValue, parentValue, moves.parentOf(link, idsOf(childValue)), dropped);
    if (trim !== null) moves.reset(link.childId, trim.value);
    const held = trim === null ? childValue : trim.value;
    const hides = hidesChild(link, parentValue, held, countWhereItHides(link, parentValue, held, moves.count), parentHidden);
    if (hides || wasHidden) {
      moves.setHidden(link.childId, hides);
      moves.tell(link.childId, { isHidden: hides, note: null, parents });
      // Hidden, it holds nothing; shown again, it starts empty. Either way
      // whatever follows it has lost its parent, and the rows under a row
      // cleared on the way go with it.
      if (hides !== wasHidden) settleChoices(graph, moves, link.childId, false, seen, trim?.dropped ?? []);
      continue;
    }
    if (trim === null) {
      moves.tell(link.childId, { parents });
      continue;
    }
    moves.tell(link.childId, {
      parents,
      note: link.childIsMany ? moves.words.removed(link, trim.removed) : moves.words.cleared(link),
    });
    for (const filled of moves.fillDefaults(link.childId)) settleChoices(graph, moves, filled, false, seen);
    settleChoices(graph, moves, link.childId, false, seen, trim.dropped);
  }
};

/** Every parent on the form settled again, after "Save and add new" reset them. */
export const settleAllChoices = (graph: ChoiceGraph, moves: ChoiceMoves): void => {
  const seen = new Set<string>();
  for (const parentId of graph.linksOfParent.keys()) settleChoices(graph, moves, parentId, false, seen);
};
